import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import Icon from "../../../../Icon/Icon"
import { EGSwitch } from "../../../../MainComponents/Switch/Switch";
import { HelpIcon } from "../../../../MainComponents/HelpIcon/HelpIcon";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import EGFileUpload from "../../../../MainComponents/FileUpload/EGFileUpload/EGFileUpload";
import { CommentFiles } from "./CommentFiles";
import { SubmitButtons } from "./CloseReport";
import { enqueueSnackbar } from "notistack";
import {
  catalogByPart,
  createAttachment,
  updateStatusCloseDetails,
} from "../../../../../apis";
import { useTranslation } from "react-i18next";

const FOUNDED_CATALOGUE = "CS-100";

export const ClosedFounded = ({
  id,
  reportDetail,
  closeDate,
  reportExpired,
  reportRender,
  editClosed,
  setEditClosed,
}) => {

  const [t] = useTranslation("report");
  const theme = useTheme();
  const lang = localStorage.getItem("lang") || "es";

  const [followUp, setFollowUp] = useState(reportDetail?.status?.details?.followup || false);

  const [observations, setObservations] = useState(reportDetail?.status?.details?.observations || "");
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState(reportDetail?.status?.details?.files || []);
  const [isLoading, setIsLoading] = useState(false);

  const [causes, setCauses] = useState(
    reportDetail?.status?.details?.causes || []
  );

  // Catalog state for founded close causes
  const [catalogOpts, setCatalogOpts] = useState(null);
  const useCatalog = Array.isArray(catalogOpts) && catalogOpts.length > 0;

  // Consultar catálogo de causas de cierre fundamentado
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const resp = await catalogByPart({
          is_own: false,
          catalogue: FOUNDED_CATALOGUE,
          path: "/",
        });
        if (!resp.error && Array.isArray(resp.data) && resp.data.length > 0) {
          const capitalize = (str) =>
            str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
          setCatalogOpts(
            resp.data.map((item) => ({
              label: capitalize(item.locales?.[lang] || item.label),
              value: item.path_locales,
              locales: item.locales || {},
            }))
          );
        }
      } catch {
        // Si falla, no se muestra select de causas
      }
    };
    fetchCatalog();
  }, []);

  const getCauseValue = (c) =>
    typeof c === "object" && c !== null ? c.value : c;

  const selectedOptions = useCatalog
    ? catalogOpts.filter((opt) =>
        causes.some((c) => getCauseValue(c) === opt.value)
      )
    : [];

  const handleSelectChange = (_e, selecteds) => {
    setCauses(
      selecteds.map((opt) => ({
        value: opt.value,
        label: opt.label,
        locales: opt.locales || {},
      }))
    );
  };

  const returnIsValid = () => {
    if (followUp && observations.trim() === "") return false;
    return true;
  }

  const handleSubmit = async (e) => {

    setIsLoading(true);

    let files_id;
    let filesToSend = [];

    enqueueSnackbar(`${t("Conclusion.sending")}...`, { variant: "info" });

    try {
      if (files.length > 0) {
        for (const fileObj of files) {
          const { formData } = fileObj;
          const filesRes = await createAttachment(formData);
          files_id = filesRes.attachments_id;
          filesToSend.push({
            file: files_id,
            name: fileObj.file.name,
            ...(!!fileObj.comment && {comment: fileObj.comment})
          });
        }
      }

      // Add current files
      if (uploadedFiles && uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          filesToSend.push({
            file: file.file,
            name: file.name,
            ...(!!file.comment && {comment: file.comment})
          });
        });
      }

      const details = {
        another_causes: [],
        followup: followUp,
        closed: "founded",
        close_date: closeDate,
        causes: useCatalog ? causes : [],
        observations,
        files: filesToSend,
        expired_reports: reportExpired,
      };

      await updateStatusCloseDetails(id, details)
      enqueueSnackbar(`${t("Conclusion.sending_successful")}`, {
        variant: "success",
      });
      reportRender();
      setIsLoading(false);
      setEditClosed(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" marginBottom={2}>
        <Box marginRight={1}>
          <Icon name="bold_routing_2" size={24} color={theme.palette.magentaBliss.main} />
        </Box>
        <Typography fontWeight={600} color="blueGray700">
          {t("Conclusion.this_report_will_followedup_another_instance")}
        </Typography>
        <HelpIcon 
          text="Texto de ayuda" 
          sx={{ marginLeft: .5}} 
        />
        <EGSwitch
          checked={followUp}
          onChange={(e) => setFollowUp(e.target.checked)}
          id="hostile"
          size="small"
          sx={{
            marginLeft: 3.5
          }}
        />
      </Stack>

      {useCatalog && (
        <Box sx={{ marginBottom: 2 }}>
          <Autocomplete
            options={catalogOpts}
            getOptionLabel={(option) => option.label}
            id="founded-causes-select"
            disableCloseOnSelect
            disableClearable
            multiple
            value={selectedOptions}
            onChange={handleSelectChange}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.value}
                  sx={{
                    backgroundColor: theme.palette.blueGray100.main,
                    fontSize: 12,
                  }}
                  size="small"
                  {...getTagProps({ index })}
                  label={
                    <Typography variant="body1" mx={1}>
                      {option.label}
                    </Typography>
                  }
                />
              ))
            }
            renderOption={(props, option, { selected }) => (
              <Box
                key={option.value}
                {...props}
                sx={{
                  maxHeight: 47,
                  p: 2,
                  my: 0.5,
                  mx: 1,
                  borderRadius: 1.5,
                  border: `${theme.palette.blueGray50.main} 1px solid`,
                }}
              >
                <Checkbox size="small" checked={selected} />
                <Typography variant="body1" mx={1}>
                  {option.label}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <EGInput
                label={t("Conclusion.founded_closed_causes")}
                placeholder={t("Conclusion.select_an_option")}
                {...params}
              />
            )}
          />
        </Box>
      )}

      <EGInput
        label={t("Conclusion.observations_type_close")}
        placeholder={t("Conclusion.observations_type_close_placeholder")}
        required={followUp}
        multiline
        rows={4}
        value={observations}
        showHelpText={false}
        showSuccessValidation
        onChange={(e) => setObservations(e.target.value)}
        sx={{
          marginBottom: 2
        }}
        {...(followUp && { isValid: observations.length > 1 ? true : false })}
      />

      <Box marginBottom={2}>
        <EGFileUpload
          title={t("Conclusion.support_fiels")}
          files={files}
          setFiles={setFiles}
          disabled={isLoading}
        />
      </Box>

      {
        (files.length > 0 || uploadedFiles.length > 0) &&
        <Stack border={`1px solid ${theme.palette.teal.main}`} borderRadius={1} marginBottom={2}>
          {
            files.length !== 0 && 
            <CommentFiles
              files={files}
              setFiles={setFiles}
              pathName="file.name"
              pathSize="file.size"
            />
          }

          {
            uploadedFiles.length !== 0 && 
            <CommentFiles
              files={uploadedFiles}
              setFiles={setUploadedFiles}
            />
          }
        </Stack>
      }

      <SubmitButtons
        isLoading={isLoading}
        disabledSave={!returnIsValid()}
        onSave={handleSubmit}
        onCancel={() => setEditClosed(false)}
        hideCancel={!editClosed}
      />

    </>
  )
}
